import type { DatasetOperationV20260601 } from '@cotera/api';
import {
  datasetOperationNewId,
  datasetOperationsEffect,
  type DatasetPreviewSkippedOperation,
} from './operations';
import type { DatasetPreviewLayer } from './types';

/** Told about writes the replay dropped for want of their column. */
export type DatasetOperationSkipReporter = (
  skipped: readonly DatasetPreviewSkippedOperation[]
) => void;

/**
 * The dataset's uncompacted operation log, replayed onto the preview table.
 *
 * The base mutating layer: it runs first, so everything after it — a pending
 * local edit, anything added later — writes over a table that already matches
 * what the server has.
 *
 * `onSkipped`, when given, is handed any writes the fold dropped because their
 * column is not on the artifact — the workflow-writeback-after-reseed case the
 * guard in `operations.ts` exists for. It runs once per materialization (the
 * stack calls `mutate` once), so the caller can turn a silent skip into a
 * telemetry event without it firing per row.
 */
export const createDatasetOperationsLayer = (
  operations: readonly DatasetOperationV20260601[],
  onSkipped?: DatasetOperationSkipReporter
): DatasetPreviewLayer => ({
  id: 'dataset-operations',
  mutate: ({ tableName, columns }) => {
    const effect = datasetOperationsEffect(operations, {
      tableName,
      columns,
      newId: datasetOperationNewId,
    });
    if (
      onSkipped !== undefined &&
      effect.skipped !== undefined &&
      effect.skipped.length > 0
    ) {
      onSkipped(effect.skipped);
    }
    return { statements: effect.statements, columns: effect.columns };
  },
});
